/** Attachment acceptance policy — SEC-MED / H5 malicious corpus rejection */

export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024; // 25 MiB alpha cap (SPEC larger later)
export const MAX_VOICE_BYTES = 8 * 1024 * 1024;

const BLOCKED_EXTENSIONS = new Set([
  "exe",
  "bat",
  "cmd",
  "com",
  "scr",
  "ps1",
  "vbs",
  "js",
  "jse",
  "wsf",
  "msi",
  "dll",
  "apk",
  "dmg",
  "pkg",
  "sh",
  "html",
  "htm",
  "svg",
]);

const ALLOWED_IMAGE = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);
const ALLOWED_AUDIO = new Set(["audio/webm", "audio/ogg", "audio/mp4", "audio/mpeg", "audio/wav"]);
const ALLOWED_DOC = new Set([
  "application/pdf",
  "text/plain",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);

export type AttachmentRejectReason =
  | "empty"
  | "too_large"
  | "blocked_extension"
  | "blocked_mime"
  | "name_traversal"
  | "polyglot_script";

export function extensionOf(fileName: string): string {
  const base = fileName.split(/[/\\]/).pop() ?? fileName;
  const i = base.lastIndexOf(".");
  return i >= 0 ? base.slice(i + 1).toLowerCase() : "";
}

export function validateAttachmentFile(input: {
  fileName: string;
  mediaType: string;
  size: number;
  kind?: "image" | "audio" | "document" | "any";
}): { ok: true } | { ok: false; reason: AttachmentRejectReason; message: string } {
  const name = input.fileName.trim();
  if (!name || input.size <= 0) {
    return { ok: false, reason: "empty", message: "Empty attachment rejected" };
  }
  if (name.includes("..") || name.includes("/") || name.includes("\\") || name.includes("\0")) {
    return { ok: false, reason: "name_traversal", message: "Unsafe file name rejected" };
  }
  const ext = extensionOf(name);
  if (BLOCKED_EXTENSIONS.has(ext)) {
    return { ok: false, reason: "blocked_extension", message: `Blocked file type .${ext}` };
  }
  const max = input.kind === "audio" ? MAX_VOICE_BYTES : MAX_ATTACHMENT_BYTES;
  if (input.size > max) {
    return { ok: false, reason: "too_large", message: `File exceeds ${Math.round(max / (1024 * 1024))} MiB limit` };
  }
  const mime = (input.mediaType || "").toLowerCase().split(";")[0]!.trim();
  if (mime.includes("javascript") || mime === "text/html" || mime === "image/svg+xml") {
    return { ok: false, reason: "blocked_mime", message: `Blocked MIME ${mime || "(empty)"}` };
  }
  // Double-extension tricks: file.pdf.exe
  const parts = name.toLowerCase().split(".");
  if (parts.length > 2 && BLOCKED_EXTENSIONS.has(parts[parts.length - 1]!)) {
    return { ok: false, reason: "blocked_extension", message: "Blocked double extension" };
  }
  // File pickers often omit MIME or use octet-stream — allow known extensions (SEC-MED-003).
  const imageExt = new Set(["jpg", "jpeg", "png", "webp", "gif"]);
  const docExt = new Set(["pdf", "txt", "docx"]);
  if (input.kind === "image") {
    const mimeOk = !mime || ALLOWED_IMAGE.has(mime) || mime === "application/octet-stream";
    if (!mimeOk && !imageExt.has(ext)) {
      return { ok: false, reason: "blocked_mime", message: `Image MIME not allowed: ${mime}` };
    }
  }
  if (input.kind === "audio" && mime && !ALLOWED_AUDIO.has(mime) && !mime.startsWith("audio/")) {
    return { ok: false, reason: "blocked_mime", message: `Audio MIME not allowed: ${mime}` };
  }
  if (input.kind === "document") {
    const mimeOk = !mime || ALLOWED_DOC.has(mime) || mime === "application/octet-stream";
    if (!mimeOk && !docExt.has(ext)) {
      return { ok: false, reason: "blocked_mime", message: `Document MIME not allowed: ${mime}` };
    }
  }
  // Polyglot: HTML/script sniff in name
  if (/\.(php|jsp|asp|aspx)$/i.test(name)) {
    return { ok: false, reason: "polyglot_script", message: "Script-like attachment rejected" };
  }
  return { ok: true };
}
